/**
 * Permission Helper
 * Shared logic for reading the USERPERMISSION_* keys stored in localStorage
 * (synced from user_role_permission at login and by the nav menu).
 *
 * Stored value: 'true'  = permission disabled
 *               'false' = permission enabled
 *               missing = not synced yet (treated as denied, except where noted)
 */

export const getPermissionState = (permName) => {
  const value = localStorage.getItem(`USERPERMISSION_${permName}`);
  if (value === null) return "missing";
  return value === "true" ? "disabled" : "enabled";
};

export const isPermissionAllowed = (permName) => {
  const state = getPermissionState(permName);

  // Explicitly disabled → always denied (respects admin toggles too)
  if (state === "disabled") return false;

  // Explicitly enabled → always allowed
  if (state === "enabled") return true;

  // Missing from localStorage (not yet seeded) → admin bypass
  if (state === "missing") {
    if (permName === "settings_suppliers" && isAdminRole()) return true;
    if (isModulePermissionKey(permName) && isAdminRole()) return true;
  }

  return false;
};

/**
 * Every permission that grants access to a report page. The Reports menu and
 * the All Reports route are available when the user holds any one of them.
 */
export const REPORT_PERMISSION_KEYS = [
  "reports",
  "reports_inventory",
  "reports_inventory_items",
  "reports_inventory_stockadjustments",
  "reports_inventory_purchase_requests",
  "reports_inventory_grn",
  "reports_inventory_material_requests",
  "reports_inventory_issue_notes",
  "reports_inventory_stock_valuation",
  "reports_inventory_low_stock",
  "reports_maintenance",
  "reports_expenses",
  "reports_expenses_cash_flow",
  "reports_expenses_payments",
  "reports_farmSetup",
  "reports_farm_plants",
];

/**
 * Material Request / Inventory Issuing / Reports module permission keys.
 * Admins are allowed access to these even before their permission rows
 * are seeded (mirrors the settings_suppliers legacy behaviour).
 */
export const isModulePermissionKey = (permName) =>
  permName === "material_request" ||
  permName.startsWith("material_request_") ||
  permName === "material_issue_note" ||
  permName.startsWith("material_issue_note_") ||
  permName === "reports" ||
  permName.startsWith("reports_") ||
  permName === "harvesting" ||
  permName.startsWith("harvesting_") ||
  permName === "production" ||
  permName.startsWith("prod_") ||
  permName === "sales_products" ||
  permName.startsWith("sales_products_");

/**
 * Returns true when the user has at least ONE of the given permissions.
 * @param {string|string[]} permNames
 */
export const isAnyPermissionAllowed = (permNames) => {
  const names = Array.isArray(permNames) ? permNames : [permNames];
  return names.some((name) => isPermissionAllowed(name));
};

/**
 * Mirror of the admin detection used by the nav menu.
 */
export const isAdminRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem("USER") || "{}");
    return (
      String(user?.roleId) === "1" ||
      String(user?.role || "").toLowerCase().includes("admin")
    );
  } catch (error) {
    return false;
  }
};

export default {
  getPermissionState,
  isPermissionAllowed,
  isAnyPermissionAllowed,
  isAdminRole,
};
